import React, { useCallback, useEffect, useState } from "react";
import { ContentWrapper, ContinueButton, RateBox, Error } from "../styles";
import { Controller, useForm } from "react-hook-form";
import InputAndSelect from "../../../../components/common/InputAndSelect";
import ErrorMessage from "../../../../components/ErrorMessage";
import { capitalizeCurrencyCode } from "../../../../utils/helpers";
import { Link } from "react-router-dom";
import { Button } from "../../../../components/common/Button";
import { savedPayoutV2Data } from "../../redux/formDataSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { getPayoutTransferFee } from "../../redux/slice";
import { formatCurrencyAmount } from "../../../../utils/currency";
import Loader from "../../../../components/common/Loader";

type AmountType = {
    amount?: number;
    handleNext?: () => void;
    currency?: string;
    totalAmount?: number;
    balanceError?: boolean;
    fees?: string;
};

const Amount = ({ handleNext }: AmountType) => {
    const dispatch = useAppDispatch();

    const { control, handleSubmit, setValue, formState: { errors, isValid }, } = useForm<AmountType>();

    const INT_FORM_DATA = useAppSelector(
        (state) => state.formData?.payoutv2Data || {},
    );

    const [localError, setLocalError] = useState(false);
    const [userData, setUserData] = useState<AmountType>(INT_FORM_DATA);
    const [showMinimumError, setShowMinimumError] = useState<boolean>();
    const [currentWallet, setCurrentWallet] = useState(null);
    const { wallets, loading } = useAppSelector((state) => state?.wallets || {});
    const getSavedData = useAppSelector((state) => state.formData.payoutv2Data);

    const minValue = 50

    useEffect(() => {
        if (userData.amount && userData?.amount < minValue) {
            setShowMinimumError(true);
        } else if (userData.totalAmount && userData.totalAmount > currentWallet?.balance) {
            setLocalError(true);
            setShowMinimumError(false);
        } else {
            setShowMinimumError(false);
            setLocalError(false);
        }
    }, [userData.amount, userData.totalAmount]);

    useEffect(() => {
        setCurrentWallet(
            wallets?.find((wallet) => wallet.currency === "KES")
        );
    }, [wallets]);

    const handleDataUpdate = (field: keyof AmountType, value: any) => {
        setUserData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    useEffect(() => {
        (async () => {
            const resultAction = await dispatch(getPayoutTransferFee("KE"));
            if (getPayoutTransferFee.fulfilled.match(resultAction)) {
                const { fees } = resultAction.payload;
                handleDataUpdate("fees", fees);
            }
        })();
    }, []);

    useEffect(() => {
        if (getSavedData) {
            Object.keys(getSavedData).forEach((key) => {
                setValue(
                    key as keyof AmountType,
                    getSavedData[key as keyof AmountType],
                );
                console.log('compare', { [key]: getSavedData[key as keyof AmountType] })
            });
        }
    }, [getSavedData, setValue]);

    const handleNextStep = useCallback(
        (data: AmountType) => {
            const combinedData = {
                ...userData,
                ...data,
            };

            dispatch(savedPayoutV2Data(combinedData));
            handleNext?.();
        },
        [dispatch, userData],
    );

    const filteredWallets = wallets as AmountType[];

    const uniqueCurrencies = Array.from(
        new Set(filteredWallets?.map((wallet) => wallet.currency)),
    );

    const options = uniqueCurrencies.map((currency) => ({
        label: currency,
        value: currency,
    }));

    const walletToDebit = options?.filter((option) => option.value === "KES");


    useEffect(() => {
        if (userData.amount) {
            handleDataUpdate(
                "totalAmount",
                Number(userData.amount) + Number(userData.fees),
            );
        }
    }, [userData.amount]);

    const disableContinueButton = userData.balanceError || loading || !isValid || localError || showMinimumError

    return (
        <ContentWrapper>
            <Loader isLoading={loading} />
            <div>
                <Controller
                    name="amount"
                    rules={{ required: "Amount is required"}}
                    control={control}
                    render={({ field }) => (
                        <InputAndSelect
                            placeholder="Amount"
                            amountValue={field.value}
                            onAmountChange={(value) => {
                                field.onChange(value)
                                handleDataUpdate("amount", value)
                            }}
                            selectValue={"KES"}
                            width="100%"
                            options={walletToDebit}
                        />
                    )}
                />
                {showMinimumError && (
                    <div className="min">
                        <ErrorMessage
                            message={`Minimum amount: ${capitalizeCurrencyCode("KES")} ${new Intl.NumberFormat().format(
                                minValue
                            )}`}
                        />
                    </div>
                )}
            </div>
            {errors?.amount && <Error>{errors?.amount?.message}</Error>}

            {userData.amount && <section>
                <RateBox>
                    <span className="fee-box">
                        +
                        <span>
                            {formatCurrencyAmount("KES", userData?.fees ?? 0, 2) ?? 0}
                        </span>
                    </span>
                    <span>Processing fee</span>
                </RateBox>
            </section>}

            {userData.amount && (
                <section>
                    <RateBox>
                        <span className="fee-box">
                            <span>
                                {formatCurrencyAmount("KES", userData.totalAmount, 2) ?? 0}
                            </span>
                        </span>
                        <span>Total amount</span>
                    </RateBox>
                </section>
            )}

            {localError && (
                <div className="min">
                    <span>Exceeds your wallet balance of {formatCurrencyAmount("KES", currentWallet?.balance, 2) ?? 0}.</span>
                    <Link to="/wallets"> Top-up funds</Link>
                </div>
            )}

            <ContinueButton>
                <span className="form-label">

                    Funds should arrive within 1-3 working days.
                </span>

                <Button
                    label="Continue"
                    height="48px"
                    fontSize="14px"
                    disabled={disableContinueButton}
                    onClick={handleSubmit(handleNextStep)}
                />
            </ContinueButton>

        </ContentWrapper>
    )
}

export default Amount